package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.ServiceItem
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft600
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_venue_back_content_description
import top.clientapp.generated.resources.client_venue_services_empty
import top.clientapp.generated.resources.client_venue_services_title
import kotlin.math.roundToInt

@Composable
fun VenueServicesCatalogContent(
    venue: VenueResponse,
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 21.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(58.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(38.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .clickable(onClick = onBackClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.ArrowLeft,
                    contentDescription = stringResource(Res.string.client_venue_back_content_description),
                    tint = MineShaft950,
                    modifier = Modifier.size(24.dp)
                )
            }

            Text(
                text = stringResource(Res.string.client_venue_services_title),
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 16.sp,
                    lineHeight = 22.sp,
                    fontWeight = FontWeight.Medium
                ),
                modifier = Modifier.padding(start = 18.dp)
            )
        }

        Text(
            text = venue.name,
            style = BodyMedium.copy(
                color = MineShaft500,
                fontSize = 20.sp,
                lineHeight = 20.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = 0.07.sp
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.padding(start = 49.dp, bottom = 22.dp)
        )

        val services = venue.services.servicesList
        if (services.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = stringResource(Res.string.client_venue_services_empty),
                    style = BodyMedium.copy(
                        color = MineShaft500,
                        fontSize = 15.sp,
                        lineHeight = 22.sp,
                        fontWeight = FontWeight.Normal
                    )
                )
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 28.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                items(
                    items = services,
                    key = { item -> "${item.name}-${item.price}-${item.description.orEmpty()}" }
                ) { service ->
                    ServiceCatalogCard(service = service)
                }
            }
        }
    }
}

@Composable
private fun ServiceCatalogCard(
    service: ServiceItem,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(White)
            .border(1.dp, MineShaft100, RoundedCornerShape(10.dp))
            .padding(horizontal = 15.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier.weight(1f)
        ) {
            Text(
                text = service.name,
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 18.sp,
                    lineHeight = 27.sp,
                    fontWeight = FontWeight.Medium,
                    letterSpacing = (-0.44).sp
                ),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )

            service.description
                ?.takeIf(String::isNotBlank)
                ?.let { description ->
                    Text(
                        text = description,
                        style = BodyMedium.copy(
                            color = MineShaft500,
                            fontSize = 14.sp,
                            lineHeight = 21.sp,
                            fontWeight = FontWeight.Normal,
                            letterSpacing = (-0.15).sp
                        ),
                        maxLines = 3,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.padding(top = 6.dp)
                    )
                }
        }

        Text(
            text = service.priceText(),
            style = BodyMedium.copy(
                color = MineShaft950,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                fontWeight = FontWeight.Medium,
                letterSpacing = (-0.31).sp
            ),
            maxLines = 1
        )
    }
}

private fun ServiceItem.priceText(): String {
    return "${price.compactPrice()} ${currency.symbol()}"
}

private fun Double.compactPrice(): String {
    val roundedInt = roundToInt()
    return if (this == roundedInt.toDouble()) {
        roundedInt.toString()
    } else {
        val value = (this * 100).roundToInt() / 100.0
        value.toString().trimEnd('0').trimEnd('.')
    }
}

private fun String.symbol(): String {
    return when (uppercase()) {
        "UAH", "UA", "₴" -> "₴"
        else -> this
    }
}
